import { Position, Link, LinkedLeaf, Leaf as ILeaf } from '../types';
import { AllowedNodeTypesArray } from '../ValueTypes/AllowedNodeTypesArray';
import { DropNode } from '../ValueTypes/DropNode';
import { HiddenNode } from '../ValueTypes/HiddenNode';
import { LeafLink } from '../ValueTypes/LeafLink';

type LeafConfig = {
  position?: Position;
  isHidden?: boolean;
  next?: ILeaf;
};

const setConfigDefaultOptions = (config: LeafConfig = {}): LeafConfig => {
  return {
    ...config,
    position: config.position ?? { x: 0, y: 0 },
    isHidden: config.isHidden ?? false,
  };
};

export abstract class Leaf implements LinkedLeaf {
  public constructor(public id: string, public type: string, allowedNodeTypes: string[], config?: LeafConfig) {
    const c = setConfigDefaultOptions(config);

    if (c.next) {
      this.allowedNodeTypes = new AllowedNodeTypesArray(allowedNodeTypes.filter(s => s !== c.next.id));
    } else {
      this.allowedNodeTypes = new AllowedNodeTypesArray(allowedNodeTypes);
    }

    this.dropNode = new DropNode(false);
    this.hidden = new HiddenNode(c.isHidden);
    this.link = new LeafLink(c.next);
    this.position = c.position;
  }

  protected allowedNodeTypes: AllowedNodeTypesArray;
  protected dropNode: DropNode;
  protected hidden: HiddenNode;
  protected link: Link;

  public position: Position;

  public getAllowedNodeTypes() {
    return Array.from(this.allowedNodeTypes);
  }

  public resetAllowedNodeTypes() {
    this.allowedNodeTypes.reset();
    return this;
  }

  public removeAllAllowedNodeTypes() {
    this.allowedNodeTypes = new AllowedNodeTypesArray([]);
    return this;
  }

  public replaceAllowedNodeTypes(allowed: string[]) {
    this.allowedNodeTypes = new AllowedNodeTypesArray(allowed);
    return this;
  }

  public getIsDropNodeShown() {
    return this.dropNode.isShown;
  }

  public hideDropNodeFor(nodeType: string) {
    if (this.allowedNodeTypes.hasType(nodeType)) {
      this.dropNode.hide();
    }

    this.link.getNext()?.hideDropNodeFor(nodeType);

    return this;
  }

  public showDropNodeFor(nodeType: string) {
    if (this.allowedNodeTypes.hasType(nodeType)) {
      this.dropNode.show();
    }

    this.link.getNext()?.showDropNodeFor(nodeType);

    return this;
  }

  public isHidden(): boolean {
    return this.hidden.isHidden();
  }

  public hideNodes() {
    this.hidden.hide();
    this.link.getNext()?.hideNodes();

    return this;
  }

  public showNodes() {
    this.hidden.show();
    this.link.getNext()?.showNodes();

    return this;
  }

  public setNextNode(node: ILeaf, useAllowedTypes = true) {
    const finalNode = this.getNodes().find(s => !s.getNextNode() && s.type !== this.type);
    if (finalNode) {
      finalNode.setNextNode(node, useAllowedTypes);
      return this;
    }

    if (useAllowedTypes && !this.allowedNodeTypes.hasType(node.type)) {
      return this;
    }

    this.link.setNext(node);
    this.allowedNodeTypes.remove(node.type);

    return this;
  }

  public replaceNextNode(node: ILeaf, forceReplace?: boolean) {
    const current = this.link.getNext();
    if ((!current || !this.allowedNodeTypes.hasType(node.type)) && !forceReplace) {
      return this;
    }

    this.link.remove();
    this.allowedNodeTypes.remove(node.type);

    this.link.setNext(node);
    this.allowedNodeTypes.add(current.type);

    return this;
  }

  public replaceNode(node: ILeaf, targetId: string) {
    if (targetId === this.link.getNext()?.id) {
      this.allowedNodeTypes.add(this.link.getNext().type);
      this.allowedNodeTypes.remove(node.type);

      node.setNextNode(this.link.getNext()?.getNextNode());
      this.link.remove();
      this.link.setNext(node);
    }

    const replace = this.getNodes().find(s => s.getNextNode()?.id === targetId);
    if (replace) {
      replace.replaceNode(node, targetId);
    }

    return this;
  }

  public removeNextNode() {
    const current = this.link.getNext();
    if (current) {
      this.allowedNodeTypes.add(current.type);
      this.link.remove();
    }

    return this;
  }

  public getNextNode() {
    return this.link.getNext();
  }

  public addNode(node: ILeaf, targetId: string, useAllowedTypes = true): this {
    const current = this.link.getNext();

    if (targetId !== this.id || (useAllowedTypes && !this.allowedNodeTypes.hasType(node.type))) {
      current?.addNode && current?.addNode(node, targetId);
      return this;
    }

    if (current && targetId === this.id) {
      node.setNextNode && node.setNextNode(current);
      this.link.remove();
      this.link.setNext(node);
      return this;
    }

    this.link.setNext(node);
    this.allowedNodeTypes.remove(node.type);

    return this;
  }

  public getNode(targetId: string): ILeaf {
    if (targetId === this.id) {
      return this;
    }

    return this.link.getNext()?.getNode(targetId);
  }

  public getNodes() {
    let nodes: ILeaf[] = [];

    nodes.push(this);

    const next = this.link.getNext();
    if (next) {
      nodes = nodes.concat(next.getNodes());
    }

    return nodes;
  }

  public removeNode(targetId: string) {
    if (targetId === this.link.getNext()?.id) {
      const currentNext = this.link.getNext();
      const newNext = this.link.getNext().getNextNode();

      this.allowedNodeTypes.add(currentNext.type, true);
      this.allowedNodeTypes.remove(newNext?.type);

      this.link.remove();
      this.link.setNext(newNext);

      return this;
    }

    const parent = this.getNodes().find(s => s.getNextNode()?.id === targetId);
    if (parent) {
      parent.removeNode(targetId);
    }

    return this;
  }
}
